import { Box } from '@mui/system';
import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import { ValueOf } from 'sl-api-connector';
import ConversionV2Card from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/ConversionV2Card';
import NewDorisMetricCard from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/NewDorisMetricCard';
import {
  DorisFieldDisplayNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { EnhancedCommonSummaryInfoWithLegend } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { KEY_METRICS_CONTAINER_ADJUSTED_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { updateUrlQueryParams } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { buildSubtitleDisplayName } from 'src/utils/filters';
import { useHistory } from 'src/utils/Hooks';

interface DorisKeyMetricCardDefinition {
  fieldName: string;
  title: string;
  onClick: () => void;
  variant: 'large' | 'small';
  metricType: ValueOf<typeof METRICTYPE>;
  methodName?: DorisMetricEndPointEnum;
  customComponent?: (card: DorisKeyMetricCardDefinition) => React.ReactNode;
  showLastweek?: boolean;
  metricTypeToDisplay: ValueOf<typeof METRICTYPE>;
  showAverageRate?: boolean;
  showMarketShare?: boolean;
}

/**
 * Shows all the key metric summary cards for the Atlas home page
 */
const AtlasV2KeyMetricsCards = () => {
  const history = useHistory();
  const theme = useStacklineTheme();

  const cardDefinitions: DorisKeyMetricCardDefinition[][] = useMemo(
    () => [
      [
        {
          fieldName: 'totalClicks',
          title: DorisFieldDisplayNameEnum.TotalClicks,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'trafficV2', subtab: 'totalClicks' })),
          variant: 'large',
          metricTypeToDisplay: METRICTYPE.VOLUME
        },
        {
          fieldName: 'organicClicks',
          title: DorisFieldDisplayNameEnum.OrganicClicks,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'trafficV2', subtab: 'organicClicks' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME
        },
        {
          fieldName: 'adClicks',
          title: DorisFieldDisplayNameEnum.AdClicks,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'trafficV2', subtab: 'adClicks' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME
        },
        {
          fieldName: 'adSpend',
          title: DorisFieldDisplayNameEnum.AdSpend,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'trafficV2', subtab: 'adClicks' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.MONEY
        },
        {
          fieldName: 'keywordsWithTotalTrafficCount',
          title: DorisFieldDisplayNameEnum.KeywordsWithTotalTrafficCount,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'trafficV2', subtab: 'totalClicks' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          showLastweek: true
        }
      ],
      // Column 2 - Conversion & Quality Metrics
      [
        {
          fieldName: 'purchaseRate',
          title: DorisFieldDisplayNameEnum.PurchaseRate,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'conversionV2', subtab: 'purchaseRate' })),
          variant: 'large',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          customComponent: (card) => {
            return (
              <ConversionV2Card
                variant={card.variant}
                fieldName={card.fieldName}
                title={card.title}
                onClick={card.onClick}
              />
            );
          }
        },
        {
          fieldName: 'retailPrice',
          title: DorisFieldDisplayNameEnum.RetailPrice,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'pricesV2', subtab: 'retailPrice' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.MONEY,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          showAverageRate: true
        },
        {
          fieldName: DorisMetricTypeEnum.AverageContentScore,
          title: DorisFieldDisplayNameEnum.ContentScore,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'contentV2', subtab: 'contentScore' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          showLastweek: true
        },
        {
          fieldName: 'reviewsRating',
          title: DorisFieldDisplayNameEnum.ReviewsRating,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'ratingV2', subtab: 'rating' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.DECIMAL,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          showLastweek: true
        },
        {
          fieldName: DorisMetricTypeEnum.PromotionCount,
          title: DorisFieldDisplayNameEnum.PromotionCount,
          shouldSliceByWeekRange: true,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'pricesV2', subtab: 'promotions' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          methodName: DorisMetricEndPointEnum.SalesEndPoint
        }
      ],
      // Column 3 - Sales & Market Share Metrics
      [
        {
          fieldName: DorisMetricTypeEnum.RetailSales,
          title: DorisFieldDisplayNameEnum.RetailSales,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'salesV2', subtab: 'retailSales' })),
          variant: 'large',
          metricTypeToDisplay: METRICTYPE.MONEY,
          methodName: DorisMetricEndPointEnum.SalesEndPoint
        },
        {
          fieldName: DorisMetricTypeEnum.UnitsSold,
          title: DorisFieldDisplayNameEnum.UnitsSold,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'salesV2', subtab: 'unitsSold' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          methodName: DorisMetricEndPointEnum.SalesEndPoint
        },
        {
          fieldName: DorisMetricTypeEnum.RetailSales,
          title: DorisFieldDisplayNameEnum.RetailSalesMarketShareV2,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'salesV2', subtab: 'retailSales' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          showMarketShare: true
        },
        {
          fieldName: DorisMetricTypeEnum.UnitsSold,
          title: DorisFieldDisplayNameEnum.UnitsSoldMarketShareV2,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'salesV2', subtab: 'unitsSold' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.PERCENT,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          showMarketShare: true
        },
        {
          fieldName: DorisMetricTypeEnum.ProductsWithSalesCount,
          title: DorisFieldDisplayNameEnum.StacklineSkuCount,
          onClick: () => history.push(updateUrlQueryParams({ tab: 'salesV2', subtab: 'retailSales' })),
          variant: 'small',
          metricTypeToDisplay: METRICTYPE.VOLUME,
          methodName: DorisMetricEndPointEnum.SalesEndPoint,
          showLastweek: true
        }
      ]
    ],
    [history]
  );

  return (
    <>
      <EnhancedCommonSummaryInfoWithLegend
        widget={{
          view: {
            buildSubTitle: ({ retailer, mainEntity, filters, categories, app }) =>
              mainEntity ? buildSubtitleDisplayName(retailer, mainEntity, filters, categories, app) : 'Summary',

            title: 'Key Metrics'
          }
        }}
      />

      <Box
        marginTop={theme.spacing.lg}
        marginBottom={theme.spacing.lg}
        sx={{
          transform: 'translateX(-16px)',
          width: KEY_METRICS_CONTAINER_ADJUSTED_WIDTH
        }}
      >
        <Flex gap={24}>
          {cardDefinitions.map((column, index) => (
            <Flex flexDirection="column" key={`column-${index}`} gap={16}>
              {column.map((card) => {
                if (card.customComponent) {
                  return card.customComponent(card);
                }
                return (
                  <NewDorisMetricCard
                    key={card.fieldName}
                    variant={card.variant}
                    queryKey={card.fieldName}
                    apiMetricType={card.fieldName}
                    methodName={card.methodName}
                    metricTypeToDisplay={card.metricTypeToDisplay}
                    title={card.title}
                    fieldName={card.fieldName}
                    onClick={card.onClick}
                    showLastweek={card.showLastweek}
                    showMarketShare={card.showMarketShare}
                    showAverageRate={card.showAverageRate}
                  />
                );
              })}
            </Flex>
          ))}
        </Flex>
      </Box>
    </>
  );
};

export default AtlasV2KeyMetricsCards;
